import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {TimePickerComponent} from '../time-picker/time-picker.component';
import {ClickOutsideServiceDirective} from '../../directives/click-outside-service.directive';
import {EscapeKeyDirective} from '../../directives/escape-key.directive';
import {DayPeriod, TimeConverter} from '../../../shared/models/time';

@Component({
  selector: 'motn-datetime-picker',
  standalone: true,
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDatepickerModule,
    MatNativeDateModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    TimePickerComponent,
    ClickOutsideServiceDirective,
    EscapeKeyDirective,
  ],
})
export class DatetimePickerComponent implements OnInit, OnChanges {
  @Input() value: Date = new Date();
  @Output() valueChange = new EventEmitter<Date>();
  @Output() closed$ = new EventEmitter<void>();

  readonly positions: ConnectedPosition[] = [
    {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8},
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8},
    {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8},
    {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8},
  ];

  maxDate = new Date();

  selectedDate: Date;

  hour: number;
  minute: number;
  second: number;
  period: DayPeriod;

  ngOnInit(): void {
    this.syncFromValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.syncFromValue();
    }
  }

  private syncFromValue(): void {
    const d = this.value ?? new Date();
    this.selectedDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const time12 = TimeConverter.from24Hour(d.getHours(), d.getMinutes(), d.getSeconds());
    this.hour = time12.hour;
    this.minute = time12.minute;
    this.second = time12.second;
    this.period = time12.period;
  }

  onDateSelected(date: Date | null): void {
    if (!date) return;
    this.selectedDate = date;
    this.emit();
  }

  onHourChange(hour: number): void {
    this.hour = hour;
    this.emit();
  }

  onMinuteChange(minute: number): void {
    this.minute = minute;
    this.emit();
  }

  onSecondChange(second: number): void {
    this.second = second;
    this.emit();
  }

  onPeriodChange(period: DayPeriod): void {
    this.period = period;
    this.emit();
  }

  private emit(): void {
    const base = this.selectedDate ?? new Date();
    const time24 = TimeConverter.to24Hour({
      hour: this.hour,
      minute: this.minute,
      second: this.second,
      period: this.period,
    });

    const result = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      time24.hour,
      time24.minute,
      time24.second,
    );

    this.valueChange.emit(result);
  }
}
